<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Addons</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Manage addon items and categories</p>
      </div>
      <div class="d-flex" style="gap: 12px;">
        <v-btn color="primary" variant="tonal" prepend-icon="mdi-shape-plus" @click="openCreateCategoryDialog">
          Add Category
        </v-btn>
        <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateItemDialog">
          Add Addon
        </v-btn>
      </div>
    </div>

    <v-row>
      <v-col
        v-for="category in addonCategories"
        :key="category.id"
        cols="12"
      >
        <AddonCategory
          :category="category"
          @edit-category="openEditCategoryDialog"
          @delete-category="openDeleteCategoryDialog"
          @edit-item="editItem"
          @delete-item="deleteItem"
        />
      </v-col>
    </v-row>
    <v-dialog v-model="editCategoryDialog" max-width="550px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-shape-outline</v-icon>
          Edit Category
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Category Name"
            v-model="editableCategory.name"
            variant="outlined"
            density="comfortable"
            class="mb-3"
          ></v-text-field>
          <v-textarea
            label="Category Description"
            v-model="editableCategory.description"
            variant="outlined"
            density="comfortable"
            rows="3"
            class="mb-3"
          ></v-textarea>
          <v-row>
            <v-col cols="6">
              <v-text-field
                label="Initial Quantity"
                v-model.number="editableCategory.initial_quantity"
                type="number"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                label="Quantity Step"
                v-model.number="editableCategory.quantity_step"
                type="number"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="editCategoryDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="saveCategory">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editItemDialog" max-width="550px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-pencil</v-icon>
          Edit Addon Item
        </v-card-title>
        <v-card-text>
          <v-text-field label="Name" v-model="editableItem.name" variant="outlined" density="comfortable" class="mb-3"></v-text-field>
          <v-text-field label="Price" v-model="editableItem.price" type="number" variant="outlined" density="comfortable" prefix="$" class="mb-3"></v-text-field>
          <v-textarea label="Note" v-model="editableItem.note" variant="outlined" density="comfortable" rows="2" class="mb-3"></v-textarea>
          <v-checkbox v-model="editableItem.imagePreview" label="Image preview" color="primary" hide-details class="mb-2"></v-checkbox>
          <v-text-field v-if="editableItem.imagePreview" label="Image Name" v-model="editableItem.imageName" variant="outlined" density="comfortable" class="mb-3"></v-text-field>
          <v-checkbox v-model="editableItem.highlightNote" label="Highlight note" color="primary" hide-details class="mb-3"></v-checkbox>
          <v-select
            label="Image comment"
            :items="imageCommentOptions"
            item-title="title"
            item-value="value"
            v-model="editableItem.imageCommentType"
            variant="outlined"
            density="comfortable"
            class="mb-3"
          ></v-select>
          <v-text-field
            v-if="editableItem.imageCommentType === 'custom'"
            label="Custom Image Comment"
            v-model="editableItem.imageCommentValue"
            variant="outlined"
            density="comfortable"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="editItemDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="saveItem">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="createItemDialog" max-width="550px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-plus-circle</v-icon>
          Create New Addon Item
        </v-card-title>
        <v-card-text>
          <v-select
            :items="addonCategories"
            item-title="name"
            item-value="id"
            label="Category"
            v-model="newItem.categoryId"
            variant="outlined"
            density="comfortable"
            class="mb-3"
          ></v-select>
          <v-text-field label="Name" v-model="newItem.name" variant="outlined" density="comfortable" class="mb-3"></v-text-field>
          <v-text-field label="Price" v-model.number="newItem.price" type="number" variant="outlined" density="comfortable" prefix="$" class="mb-3"></v-text-field>
          <v-textarea label="Note" v-model="newItem.note" variant="outlined" density="comfortable" rows="2" class="mb-3"></v-textarea>
          <v-checkbox v-model="newItem.imagePreview" label="Image preview" color="primary" hide-details class="mb-2"></v-checkbox>
          <v-text-field v-if="newItem.imagePreview" label="Image Name" v-model="newItem.imageName" variant="outlined" density="comfortable" class="mb-3"></v-text-field>
          <v-checkbox v-model="newItem.highlightNote" label="Highlight note" color="primary" hide-details class="mb-3"></v-checkbox>
          <v-select
            label="Image comment"
            :items="imageCommentOptions"
            item-title="title"
            item-value="value"
            v-model="newItem.imageCommentType"
            variant="outlined"
            density="comfortable"
            class="mb-3"
          ></v-select>
          <v-text-field
            v-if="newItem.imageCommentType === 'custom'"
            label="Custom Image Comment"
            v-model="newItem.imageCommentValue"
            variant="outlined"
            density="comfortable"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="createItemDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="createItem">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="createCategoryDialog" max-width="550px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-shape-plus</v-icon>
          Create New Category
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Category Name"
            v-model="newCategory.name"
            variant="outlined"
            density="comfortable"
            class="mb-3"
          ></v-text-field>
          <v-textarea
            label="Category Description"
            v-model="newCategory.description"
            variant="outlined"
            density="comfortable"
            rows="3"
            class="mb-3"
          ></v-textarea>
          <v-row>
            <v-col cols="6">
              <v-text-field
                label="Initial Quantity"
                v-model.number="newCategory.initial_quantity"
                type="number"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                label="Quantity Step"
                v-model.number="newCategory.quantity_step"
                type="number"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="createCategoryDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="createCategory">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteCategoryDialog" max-width="420px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          Delete Category
        </v-card-title>
        <v-card-text class="text-body-1">
          This will also delete all the addon items within this category. This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteCategoryDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deleteCategoryConfirmed">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { addonService } from '@/services/addonService';
import { useNotificationStore } from '@/stores';
import AddonCategory from '@/components/AddonCategory.vue';

const notificationStore = useNotificationStore();

const addonCategories = ref([]);
const editCategoryDialog = ref(false);
const editableCategory = reactive({ id: null, name: '', description: '', initial_quantity: 1, quantity_step: 1 });
const editableCategoryIndex = ref(-1);
const deleteCategoryDialog = ref(false);
const categoryToDelete = ref(null);
const categoryToDeleteIndex = ref(-1);
const editItemDialog = ref(false);
const editableItem = reactive({ id: null, name: '', price: 0, note: '', categoryId: null, imagePreview: false, imageName: '', highlightNote: false, imageCommentType: '', imageCommentValue: '' });
const editableItemCategory = ref(null);
const createItemDialog = ref(false);
const newItem = reactive({
  name: '',
  price: 0,
  note: '',
  categoryId: null,
  imagePreview: false,
  imageName: '',
  highlightNote: false,
  imageCommentType: '',
  imageCommentValue: '',
});
const createCategoryDialog = ref(false);
const newCategory = reactive({
  name: '',
  description: '',
  initial_quantity: 1,
  quantity_step: 1,
});
const imageCommentOptions = [
  { title: 'None', value: '' },
  { title: 'Default', value: 'default' },
  { title: 'Custom', value: 'custom' },
];

onMounted(async () => {
  try {
    const response = await addonService.getAddons();
    addonCategories.value = response.data || [];
  } catch (error) {
    notificationStore.handleApiError(error, 'Failed to load addons');
  }
});

function openEditCategoryDialog(category, index) {
  Object.assign(editableCategory, category);
  editableCategoryIndex.value = index;
  editCategoryDialog.value = true;
}
async function saveCategory() {
  try {
    await addonService.updateAddonCategory(editableCategory.id, {
      name: editableCategory.name,
      description: editableCategory.description,
      initial_quantity: editableCategory.initial_quantity,
      quantity_step: editableCategory.quantity_step,
    });
    addonCategories.value.splice(editableCategoryIndex.value, 1, { ...editableCategory });
    editCategoryDialog.value = false;
    notificationStore.showSuccess('Category updated successfully');
  } catch (error) {
    notificationStore.handleApiError(error, 'Failed to update category');
  }
}
function openDeleteCategoryDialog(category, index) {
  categoryToDelete.value = category;
  categoryToDeleteIndex.value = index;
  deleteCategoryDialog.value = true;
}
async function deleteCategoryConfirmed() {
  try {
    await addonService.deleteAddonCategory(categoryToDelete.value.id);
    addonCategories.value.splice(categoryToDeleteIndex.value, 1);
    deleteCategoryDialog.value = false;
    notificationStore.showSuccess('Category deleted successfully');
  } catch (error) {
    notificationStore.handleApiError(error, 'Failed to delete category');
  }
}
function editItem(item, category) {
  const itemCopy = {
    imagePreview: false,
    imageName: '',
    highlightNote: false,
    ...item,
  };
  if (itemCopy.imageComment === 'default') {
    itemCopy.imageCommentType = 'default';
    itemCopy.imageCommentValue = '';
  } else if (itemCopy.imageComment) {
    itemCopy.imageCommentType = 'custom';
    itemCopy.imageCommentValue = itemCopy.imageComment;
  } else {
    itemCopy.imageCommentType = '';
    itemCopy.imageCommentValue = '';
  }
  Object.assign(editableItem, itemCopy);
  editableItemCategory.value = category;
  editItemDialog.value = true;
}
async function saveItem() {
  try {
    const imageComment = editableItem.imageCommentType === 'custom'
      ? editableItem.imageCommentValue
      : editableItem.imageCommentType;
    const payload = {
      name: editableItem.name,
      price: editableItem.price,
      note: editableItem.note,
      image_preview: editableItem.imagePreview,
      image_name: editableItem.imageName,
      image_comment: imageComment,
      highlight_note: editableItem.highlightNote,
      category_id: editableItem.categoryId,
    };
    await addonService.updateAddonItem(editableItem.id, payload);
    const itemIndex = editableItemCategory.value.items.findIndex(i => i.id === editableItem.id);
    if (itemIndex > -1) {
      editableItemCategory.value.items.splice(itemIndex, 1, { ...editableItem, imageComment });
    }
    editItemDialog.value = false;
    notificationStore.showSuccess('Item updated successfully');
  } catch (error) {
    notificationStore.handleApiError(error, 'Failed to update item');
  }
}
async function deleteItem(category, item) {
  if (confirm(`Are you sure you want to delete ${item.name}?`)) {
    try {
      await addonService.deleteAddonItem(item.id);
      const itemIndex = category.items.findIndex(i => i.id === item.id);
      if (itemIndex > -1) {
        category.items.splice(itemIndex, 1);
      }
      notificationStore.showSuccess('Item deleted successfully');
    } catch (error) {
      notificationStore.handleApiError(error, 'Failed to delete addon item');
    }
  }
}
function openCreateItemDialog() {
  Object.assign(newItem, { name: '', price: 0, note: '', categoryId: null, imagePreview: false, imageName: '', highlightNote: false, imageCommentType: '', imageCommentValue: '' });
  createItemDialog.value = true;
}
async function createItem() {
  if (!newItem.categoryId) {
    notificationStore.showWarning('Please select a category');
    return;
  }
  try {
    const imageComment = newItem.imageCommentType === 'custom'
      ? newItem.imageCommentValue
      : newItem.imageCommentType;
    const payload = {
      name: newItem.name,
      price: newItem.price,
      note: newItem.note,
      image_preview: newItem.imagePreview,
      image_name: newItem.imageName,
      image_comment: imageComment,
      highlight_note: newItem.highlightNote,
      category_id: newItem.categoryId,
    };
    const createdItem = await addonService.createAddonItem(payload);
    const category = addonCategories.value.find(c => c.id === newItem.categoryId);
    if (category) {
      category.items.push(createdItem);
    }
    createItemDialog.value = false;
    notificationStore.showSuccess('Addon item created successfully');
  } catch (error) {
    notificationStore.handleApiError(error, 'Failed to create addon item');
  }
}
function openCreateCategoryDialog() {
  Object.assign(newCategory, { name: '', description: '', initial_quantity: 1, quantity_step: 1 });
  createCategoryDialog.value = true;
}
async function createCategory() {
  try {
    const createdCategory = await addonService.createAddonCategory(newCategory);
    addonCategories.value.push(createdCategory);
    createCategoryDialog.value = false;
    notificationStore.showSuccess('Category created successfully');
  } catch (error) {
    notificationStore.handleApiError(error, 'Failed to create category');
  }
}
</script>

<style scoped>
</style> 